import React, { useState, useEffect, useContext } from "react";
import * as func from "../../api/apiHelper";
import { downloadExport } from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPenToSquare,
  faSearch,
  faEye,
  faCheckCircle,
  faHourglassHalf,
  faIdCard,
  faSlidersH,
  faXmark,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { convertToIST } from "../../utilities";
import { PVContext } from "../context/profileVerificationContext";

const STATUS_MAP = {
  PENDING: { cls: "bg-blue-100 text-blue-700", label: "Pending" },
  APPROVED: { cls: "bg-green-100 text-green-700", label: "Approved" },
  REJECTED: { cls: "bg-red-100 text-red-600", label: "Rejected" },
};

const sampleFilterData = {
  status: "",
  name: "",
  mobile: "",
  dob: "",
  startDate: "",
  endDate: "",
  createdAt: "",
  updatedAt: "",
  verificationType: "",
};

const VerificationRequests = () => {
  const {
    pvSearchTerm: globalSearchTerm,
    setPvSearchTerm: setGlobalSearchTerm,
    pvFilters: globalFilters,
    setPvFilters: setGlobalFilters,
    ContextCurrentPage: currentPage,
    contextSetCurrentPage: setCurrentPage,
    ContextTotalPages: totalPages,
    contextSetTotalPages: setTotalPages,
    Contextlimit: limit,
    ContextReload: reload,
    ContextSetReload: setReload,
    verificationType,
    setVerificationType,
  } = useContext(PVContext);

  const [verificationRequests, setVerificationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editRequestData, setEditRequestData] = useState({});
  const [showEditButton, setShowEditButton] = useState(false);
  const [viewRequestPopup, setViewRequestPopup] = useState(false);
  const [viewRequestData, setViewRequestData] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    const fetchRequests = async () => {
      setLoading(true);
      const params = { page: currentPage, limit, searchTerm: globalSearchTerm };
      try {
        const res = await func.post(
          urls.LIST_ALL_VERIFICATION_REQUESTS_URL,
          { ...globalFilters, verificationType },
          { params, signal: controller.signal }
        );
        if (res?.status === 200) {
          setVerificationRequests(res?.data?.data || []);
          setTotalPages(res?.data?.pagination?.totalPages || 1);
        } else {
          toast.error("Failed to load verification requests");
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        toast.error("Failed to load verification requests");
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
    return () => controller.abort();
  }, [reload, currentPage]);

  const handleSearch = () => { setReload(!reload); setCurrentPage(1); };

  const applyFilters = () => { setReload(!reload); setShowFilter(false); setGlobalSearchTerm(""); setCurrentPage(1); };

  const resetFilters = () => {
    setGlobalFilters(sampleFilterData);
    setGlobalSearchTerm("");
    setReload(!reload);
    setCurrentPage(1);
  };

  const handleEditRequest = async () => {
    setShowEditButton(true);
    if (!editRequestData?._id) { toast.warn("Request ID required"); setShowEditButton(false); return; }
    if (!editRequestData?.status) { toast.warn("Status required"); setShowEditButton(false); return; }
    if (editRequestData?.status === "REJECTED" && !editRequestData.reasonForRejection?.trim()) {
      toast.warn("Rejection reason required");
      setShowEditButton(false);
      return;
    }
    try {
      const payload = {
        status: editRequestData.status,
        rejectionReason: editRequestData.status === "REJECTED" ? editRequestData.reasonForRejection.trim() : null,
      };
      const res = await func.put(`${urls.UPDATE_VERIFICATION_REQUESTS_URL}${editRequestData._id}`, payload);
      if (res?.status === 200) {
        toast.success(res?.data?.message || "Status updated successfully");
        setShowEditPopup(false);
        setReload((p) => !p);
      } else {
        toast.error("Failed to update status");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "An error occurred");
    } finally {
      setShowEditButton(false);
    }
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faIdCard} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Verification Requests</h1>
              <p className="text-white/70 text-sm">Aadhaar verification requests</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadExport('mst-admin/export/verifications', { status: globalFilters.status }, 'verifications_export.csv')}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export
            </button>
            <button
              onClick={() => setShowFilter(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
            >
              <FontAwesomeIcon icon={faSlidersH} />
              Filters
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={globalSearchTerm}
              onChange={(e) => { setGlobalSearchTerm(e.target.value); if (!e.target.value.trim()) handleSearch(); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              maxLength={60}
              placeholder="Search verification requests..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            Search
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Name / Mobile", "DOB", "Aadhaar No", "Status", "Created / Updated", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : verificationRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faIdCard} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No verification requests found
                    </td>
                  </tr>
                ) : (
                  verificationRequests.map((req, i) => {
                    const statusCfg = STATUS_MAP[req.status] || { cls: "bg-gray-100 text-gray-500", label: req.status };
                    return (
                      <tr key={req._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-400 text-xs">{(currentPage - 1) * limit + i + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{req?.name || "—"}</p>
                          <p className="text-xs text-gray-400">{req?.mobile || ""}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{req?.dob ? convertToIST(req?.dob) : "—"}</td>
                        <td className="px-4 py-3 text-gray-600">{req?.aadharno || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.cls}`}>
                            <FontAwesomeIcon icon={req.status === "PENDING" ? faHourglassHalf : faCheckCircle} className="text-[10px]" />
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          <p>{new Date(req.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                          <p className="text-gray-400">{new Date(req.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => { setViewRequestData(req); setViewRequestPopup(true); }}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition"
                            >
                              <FontAwesomeIcon icon={faEye} />
                              View
                            </button>
                            <button
                              onClick={() => { setEditRequestData(req); setShowEditPopup(true); }}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-5">
            <PagBtn onClick={() => setCurrentPage(1)} disabled={currentPage === 1} label="«" />
            <PagBtn onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} icon={faChevronLeft} />
            {currentPage > 3 && <span className="px-2 text-white/60">…</span>}
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 rounded-md text-sm font-medium transition ${
                  p === currentPage ? "bg-white text-indigo-700" : "text-white/70 hover:bg-white/10"
                }`}
              >
                {p}
              </button>
            ))}
            {currentPage < totalPages - 2 && <span className="px-2 text-white/60">…</span>}
            <PagBtn onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} icon={faChevronRight} />
            <PagBtn onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} label="»" />
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowFilter(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">Filter Requests</h3>
              <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input type="text" value={globalFilters.name} onChange={(e) => setGlobalFilters({ ...globalFilters, name: e.target.value })} placeholder="Enter name" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Mobile</label>
                <input type="number" value={globalFilters.mobile} onChange={(e) => setGlobalFilters({ ...globalFilters, mobile: e.target.value })} onWheel={(e) => e.target.blur()} placeholder="Mobile number" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
                <input type="date" value={globalFilters.dob} onChange={(e) => setGlobalFilters({ ...globalFilters, dob: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Aadhaar Number</label>
                <input type="number" value={globalFilters.aadharNo} onChange={(e) => setGlobalFilters({ ...globalFilters, aadharNo: e.target.value })} onWheel={(e) => e.target.blur()} placeholder="Aadhaar number" className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                <input type="date" value={globalFilters.startDate} onChange={(e) => setGlobalFilters({ ...globalFilters, startDate: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                <input type="date" value={globalFilters.endDate} onChange={(e) => setGlobalFilters({ ...globalFilters, endDate: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select value={globalFilters.status} onChange={(e) => setGlobalFilters({ ...globalFilters, status: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white">
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
              <button onClick={resetFilters} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Reset</button>
              <button onClick={applyFilters} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Status Modal */}
      {showEditPopup && editRequestData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowEditPopup(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Update Request Status</h3>
              <button onClick={() => setShowEditPopup(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={editRequestData?.status || ""}
                  onChange={(e) => setEditRequestData((prev) => ({ ...prev, status: e.target.value, reasonForRejection: e.target.value === "REJECTED" ? "" : prev.reasonForRejection }))}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white"
                >
                  <option value="">Select Status</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
              {editRequestData?.status === "REJECTED" && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Reason for Rejection</label>
                  <textarea
                    value={editRequestData?.reasonForRejection || ""}
                    onChange={(e) => setEditRequestData((prev) => ({ ...prev, reasonForRejection: e.target.value }))}
                    rows={3}
                    placeholder="Enter rejection reason..."
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 resize-none"
                  />
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowEditPopup(false)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Cancel</button>
              <button
                onClick={handleEditRequest}
                disabled={showEditButton}
                className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Request Modal */}
      {viewRequestPopup && viewRequestData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setViewRequestPopup(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">Request Details</h3>
              <button onClick={() => setViewRequestPopup(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <InfoField label="Name" value={viewRequestData?.name} />
                <InfoField label="DOB" value={convertToIST(viewRequestData?.dob)} />
                <InfoField label="Mobile" value={viewRequestData?.mobile} />
                <InfoField label="Status" value={viewRequestData?.status} />
                {viewRequestData?.status === "REJECTED" && (
                  <div className="col-span-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Reason for Rejection</label>
                    <textarea value={viewRequestData?.reasonForRejection || "—"} readOnly rows={3} className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-gray-50 resize-none" />
                  </div>
                )}
                <div className="col-span-3 pt-2 pb-1 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Aadhaar Details</p>
                </div>
                <InfoField label="Full Name" value={viewRequestData?.aadharDetails?._aadharUserDetails?.full_name} />
                <InfoField label="Aadhaar DOB" value={viewRequestData?.aadharDetails?._aadharUserDetails?.dob} />
                <InfoField label="Gender" value={viewRequestData?.aadharDetails?._aadharUserDetails?.gender} />
                {viewRequestData?.aadharDetails?._aadharUserDetails?.address && (
                  <div className="col-span-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Address</label>
                    <textarea
                      readOnly
                      rows={5}
                      value={(() => {
                        const a = viewRequestData.aadharDetails._aadharUserDetails.address;
                        return [a.house, a.street, a.landmark && `Landmark: ${a.landmark}`, a.loc, a.vtc, `Sub-Dist: ${a.subdist}`, `Dist: ${a.dist}`, a.state, a.zip, a.country].filter(Boolean).join(", ");
                      })()}
                      className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-gray-50 resize-none"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
              <button onClick={() => setViewRequestPopup(false)} className="w-full py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <div className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-md border border-gray-100">{value || "—"}</div>
  </div>
);

const PagBtn = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition ${
      disabled ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:bg-white/10"
    }`}
  >
    {icon ? <FontAwesomeIcon icon={icon} className="text-xs" /> : label}
  </button>
);

export default VerificationRequests;
